/**
 * Talking to Stripe.
 *
 * Stripe.js is loaded from js.stripe.com at runtime rather than bundled. That
 * is not a packaging preference — Stripe requires the script to be served
 * from their domain, and it is what keeps the card fields inside an iframe
 * they control. The card number never enters this application's JavaScript,
 * which is the difference between filing PCI SAQ A and SAQ D.
 *
 * It also means no npm dependency to install.
 */

/** Cards are capped here, in the worker, and by a check constraint on the
    payments table. Three places, because this one bounds how much a
    chargeback can take back. */
export const CARD_MAX_CENTS = 500_000;

const PUBLISHABLE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const WORKER = import.meta.env.VITE_PAYMENTS_WORKER as string | undefined;

export const cardPaymentsConfigured = () => Boolean(PUBLISHABLE && WORKER);

/** The worker on its own. The AI helpers run through it and need nothing from
    Stripe, so they should not disappear because a publishable key is missing. */
export const workerConfigured = () => Boolean(WORKER);

/* Minimal shapes for the parts of Stripe.js used here. The real types come
   from @stripe/stripe-js, which is not installed because the script is not
   bundled. */
interface StripeElement {
  mount(selector: string | HTMLElement): void;
  unmount(): void;
  destroy(): void;
  on(event: string, handler: (e: { error?: { message: string }; complete?: boolean }) => void): void;
}
interface StripeElements {
  create(type: string, options?: Record<string, unknown>): StripeElement;
}
export interface StripeLike {
  elements(options?: Record<string, unknown>): StripeElements;
  confirmCardPayment(clientSecret: string, data: Record<string, unknown>): Promise<{
    error?: { message: string };
    paymentIntent?: { id: string; status: string };
  }>;
}

let loading: Promise<StripeLike> | null = null;

export function loadStripe(): Promise<StripeLike> {
  if (!PUBLISHABLE) {
    return Promise.reject(new Error(
      "Card payments are not set up. Add VITE_STRIPE_PUBLISHABLE_KEY."));
  }
  if (loading) return loading;

  loading = new Promise<StripeLike>((resolve, reject) => {
    const w = window as unknown as { Stripe?: (k: string) => StripeLike };
    if (w.Stripe) { resolve(w.Stripe(PUBLISHABLE)); return; }

    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => {
      const s = (window as unknown as { Stripe?: (k: string) => StripeLike }).Stripe;
      if (s) resolve(s(PUBLISHABLE));
      else reject(new Error("Stripe loaded but did not start."));
    };
    script.onerror = () => {
      loading = null;   // let a later attempt try again
      reject(new Error("Could not reach Stripe. Check the connection."));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/* ------------------------------------------------------------------ */
/* The worker                                                          */
/* ------------------------------------------------------------------ */

async function callWorker<T>(path: string, body: unknown): Promise<T> {
  if (!WORKER) throw new Error("Card payments are not set up. Add VITE_PAYMENTS_WORKER.");
  const res = await fetch(`${WORKER.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error ?? `The payment service returned ${res.status}.`);
  return data as T;
}

/** Ask for a charge. The worker checks the cap and the deal's balance before
    Stripe is told anything. */
export const createIntent = (dealId: string, amountCents: number) =>
  callWorker<{ clientSecret: string; intentId: string }>("/intent", { dealId, amountCents });

/** Ask the worker to write the payment row. It verifies with Stripe first, so
    a browser cannot invent a payment by calling this. */
export const recordPayment = (paymentIntentId: string) =>
  callWorker<{ ok: true; alreadyRecorded?: boolean }>("/record", { paymentIntentId });

/** Refund a card payment in full and mark it reversed. The worker verifies
    the payment is a card with a Stripe reference before anything moves. */
export const refundPayment = (paymentId: string, reason: string) =>
  callWorker<{ ok: true; refundId?: string; alreadyRefunded?: boolean }>(
    "/refund", { paymentId, reason });

/** Ask the worker to draft an equipment list from photographs. The Anthropic
    key lives there, never here. */
export const describeVehicle = (images: string[], vehicle: {
  year: number; make: string; model: string; trim: string | null;
}) => callWorker<{ text: string }>("/describe", { images, vehicle });

/** Shorten a public link. Returns null when the shortener cannot be reached —
    the caller uses the original, which works either way. */
export const shortenUrl = (url: string) =>
  callWorker<{ short: string | null }>("/shorten", { url });

/** Draft the clauses for a We Owe from a one-line note. A draft only — it
    lands in an editable box and nobody signs anything the dealer has not
    read. */
/** Email a PDF to the customer. The bytes go up base64; the worker holds the
    mail key and records the send against the deal. */
export const emailDocument = (payload: {
  to: string; subject: string; message: string;
  filename: string; pdfBase64: string; dealId: string;
  /** Shown in the header block of the email, so the customer can tell one
      message from another at a glance. */
  context?: { dealNo?: string; vehicle?: string; vin?: string };
}) => callWorker<{ ok: true; id: string | null; warning?: string }>("/email", payload);

export const draftWeOwe = (note: string, context: {
  vehicle?: string; vin?: string; buyer?: string; dealer?: string; today?: string;
}) => callWorker<{ title: string; body: string }>("/weowe", { note, context });

/* ------------------------------------------------------------------ */
/* Charges — a card taken outside a deal                               */
/* ------------------------------------------------------------------ */

/** What the customer's side of a payment link is told. */
export interface OpenCharge {
  chargeNo: string;
  amountCents: number;
  description: string | null;
  status: "open" | "paid" | "refunded" | "voided";
  paidAt: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  customerName: string;
  vehicle: string | null;
  vin: string | null;
  dealer: {
    legalName: string; dba: string | null;
    street: string; city: string; state: string; zip: string;
    phone: string; email: string; flhsmvLicenseNo: string;
  } | null;
}

/** Open a payment link. The token is the only credential — see 32_charges.sql
    for why there is no code to read out as well. */
export const openCharge = (token: string) =>
  callWorker<OpenCharge & { ok: true }>("/charge/open", { token });

/** Ask for the charge's intent. The amount comes out of the row, so neither
    argument here can influence what is charged. */
export const createChargeIntent = (ref: { chargeId: string } | { token: string }) =>
  callWorker<{ clientSecret: string; intentId: string; amountCents: number }>(
    "/charge/intent", ref);

/** The row as written. Enough to put a receipt together without reading the
    charge back, which a payment link cannot do: paying retires the token. */
export interface PaidCharge {
  charge_no: string;
  amount_cents: number;
  paid_at: string | null;
  card_brand: string | null;
  card_last4: string | null;
}

/** Write it down, after the worker has asked Stripe whether it happened. */
export const recordChargePayment = (paymentIntentId: string) =>
  callWorker<{ ok: true; alreadyRecorded?: boolean; charge?: PaidCharge }>(
    "/charge/record", { paymentIntentId });

/** Refund in full and mark the charge refunded. */
export const refundCharge = (chargeId: string, reason: string) =>
  callWorker<{ ok: true; refundId?: string; alreadyRefunded?: boolean }>(
    "/charge/refund", { chargeId, reason });

/** Email a charge receipt. Logged against the charge, not a deal. */
export const emailChargeReceipt = (payload: {
  to: string; subject: string; message: string;
  chargeId: string;
  /** Omitted for the payment-link email: there is nothing to attach until it
      has been paid. */
  filename?: string; pdfBase64?: string;
  context?: { vehicle?: string; vin?: string };
}) => callWorker<{ ok: true; id: string | null; warning?: string }>("/email", payload);
